import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import { Container, Row, Col, Card, Form, Button, Alert, Tab, Nav, Spinner } from 'react-bootstrap';
import alertIcon from '../assets/icons/alert-triangle.svg';
import checkIcon from '../assets/icons/check.svg';
import closeIcon from '../assets/icons/close.svg';

/**
 * AdminSettings component allows admin-only configuration
 * Backed by system_settings table via GET/PUT /admin/settings
 */
export function AdminSettings() {
  const { user, apiCall } = useOutletContext();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({});
  const [saveMessage, setSaveMessage] = useState(null);
  const [saving, setSaving] = useState(false);

  // Fetch user info when component mounts
  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const response = await apiCall(`${API_BASE_URL}/auth/me`);
        if (response.success && response.user) {
          setUserInfo({
            id: response.user.id,
            email: response.user.email,
            role: response.user.role,
            name: response.user.name,
          });
        } else {
          setUserInfo(user);
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        setUserInfo(user);
      }
    };

    fetchUserInfo();
  }, [apiCall, user]);

  // Fetch system settings
  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const response = await apiCall(`${API_BASE_URL}/admin/settings`);
        if (response.success && response.data) {
          // Convert array of { key, value, data_type } to flat object
          const settingsMap = {};
          for (const row of response.data) {
            if (row.data_type === 'number') {
              settingsMap[row.key] = parseInt(row.value) || 0;
            } else if (row.data_type === 'boolean') {
              settingsMap[row.key] = row.value === 'true';
            } else {
              settingsMap[row.key] = row.value;
            }
          }
          setSettings(settingsMap);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [apiCall]);

  const handleSettingChange = (key, value) => {
    setSettings({ ...settings, [key]: value });
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      const response = await apiCall(`${API_BASE_URL}/admin/settings`, {
        method: 'PUT',
        body: JSON.stringify({ settings }),
      });
      if (response.success) {
        setSaveMessage({ type: 'success', text: 'Settings saved successfully!' });
      } else {
        setSaveMessage({ type: 'danger', text: response.error || 'Failed to save settings' });
      }
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setSaveMessage({ type: 'danger', text: error.message || 'Failed to save settings' });
    } finally {
      setSaving(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center py-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2 text-muted">Loading settings...</p>
        </div>
      </Container>
    );
  }

  // Check if user has admin role
  if (userInfo?.role !== 'admin') {
    return (
      <Container fluid className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>This page is only accessible to administrators.</p>
        </Alert>
      </Container>
    );
  }

  const hourOptions = Array.from({ length: 24 }, (_, i) => (
    <option key={i} value={i}>
      {i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}
    </option>
  ));

  return (
    <Container fluid className="py-4">
      <Row className="mb-4">
        <Col>
          <h2>Admin Settings</h2>
          <p className="text-muted">Configure system-wide settings</p>
        </Col>
      </Row>

      {saveMessage && (
        <Alert variant={saveMessage.type} dismissible onClose={() => setSaveMessage(null)}>
          {saveMessage.text}
        </Alert>
      )}

      <Tab.Container defaultActiveKey="notifications">
        <Row>
          <Col md={3}>
            <Nav variant="pills" className="flex-column">
              <Nav.Item>
                <Nav.Link eventKey="notifications">Notifications</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="general">General Settings</Nav.Link>
              </Nav.Item>
            </Nav>
          </Col>

          <Col md={9}>
            <Tab.Content>
              {/* Notifications Tab */}
              <Tab.Pane eventKey="notifications">
                <Card>
                  <Card.Header>
                    <h3>Notification Settings</h3>
                  </Card.Header>
                  <Card.Body>
                    <Form>
                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Enable Notifications</Form.Label>
                            <Form.Select
                              value={settings.notification_enabled ? 'true' : 'false'}
                              onChange={(e) => handleSettingChange('notification_enabled', e.target.value === 'true')}
                            >
                              <option value="true">Enabled</option>
                              <option value="false">Disabled</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                              Global toggle for all push notifications
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>SMS Failover Delay (minutes)</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              max={120}
                              value={settings.notification_sms_failover_minutes || 15}
                              onChange={(e) => handleSettingChange('notification_sms_failover_minutes', parseInt(e.target.value) || 15)}
                            />
                            <Form.Text className="text-muted">
                              Minutes before SMS is sent if push notification isn't acknowledged
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Do Not Disturb Start</Form.Label>
                            <Form.Select
                              value={settings.notification_dnd_start_hour ?? 22}
                              onChange={(e) => handleSettingChange('notification_dnd_start_hour', parseInt(e.target.value))}
                            >
                              {hourOptions}
                            </Form.Select>
                            <Form.Text className="text-muted">
                              Global DND start hour (agents can override)
                            </Form.Text>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group className="mb-3">
                            <Form.Label>Do Not Disturb End</Form.Label>
                            <Form.Select
                              value={settings.notification_dnd_end_hour ?? 7}
                              onChange={(e) => handleSettingChange('notification_dnd_end_hour', parseInt(e.target.value))}
                            >
                              {hourOptions}
                            </Form.Select>
                            <Form.Text className="text-muted">
                              Global DND end hour (agents can override)
                            </Form.Text>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Card bg="light" className="mb-3">
                        <Card.Body>
                          <Card.Title>
                            {settings.notification_enabled
                              ? <><img src={checkIcon} alt="" style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle', display: 'inline-block' }} />Notifications Active</>
                              : <><img src={closeIcon} alt="" style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle', display: 'inline-block' }} />Notifications Disabled</>}
                          </Card.Title>
                          <Card.Text className="text-muted">
                            {settings.notification_enabled
                              ? `SMS failover after ${settings.notification_sms_failover_minutes || 15} min. DND: ${settings.notification_dnd_start_hour ?? 22}:00 - ${settings.notification_dnd_end_hour ?? 7}:00`
                              : 'All push notifications are currently disabled system-wide'}
                          </Card.Text>
                        </Card.Body>
                      </Card>

                      <Button variant="primary" onClick={handleSaveSettings} disabled={saving}>
                        {saving ? <Spinner size="sm" className="me-2" /> : null}
                        Save Settings
                      </Button>
                    </Form>
                  </Card.Body>
                </Card>
              </Tab.Pane>

              {/* General Settings Tab */}
              <Tab.Pane eventKey="general">
                <Card className="mb-4">
                  <Card.Header>
                    <h3>System Configuration</h3>
                  </Card.Header>
                  <Card.Body>
                    <Alert variant="info">
                      <img src={alertIcon} alt="" style={{ width: 18, height: 18, marginRight: 6, verticalAlign: 'middle', display: 'inline-block' }} />
                      Additional general settings (rental rates, vehicle limits, etc.) will be added here as they are implemented.
                    </Alert>
                  </Card.Body>
                </Card>
              </Tab.Pane>
            </Tab.Content>
          </Col>
        </Row>
      </Tab.Container>
    </Container>
  );
}

export default AdminSettings;
